import { useCallback, useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import { toast } from 'sonner';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { MEDIA_AVAILABLE } from '@/api/authenticatedMedia';
import { loadDocumentResource, documentFailureMessage } from '@/utils/documentResource';

const identity = request => JSON.stringify([request.key, request.requestedUrl || request.url,
  request.url, !!request.offlineCopy, request.orderNumber || '']);
const escapeHtml = value => String(value || '').replace(/[&<>"']/g, character => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[character]));
const close = popup => { try { if (!popup.closed) popup.close(); } catch { /* Already inaccessible. */ } };

// Published windows own their resource independently of the originating React
// caller. A native PDF navigation/load must not revoke its still-needed URL.
function retainPublishedResource(popup, objectUrl) {
  let released = false;
  const timer = setInterval(() => {
    if (popup.closed && !released) {
      released = true;
      clearInterval(timer);
      URL.revokeObjectURL(objectUrl);
    }
  }, 1000);
}

export default function useDocumentPopup({ requests }) {
  const session = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const latest = useRef(null);
  latest.current = { requests, session };
  const mounted = useRef(false);
  const operations = useRef(new Map());
  useLayoutEffect(() => {
    mounted.current = true;
    const owned = operations.current;
    return () => {
      mounted.current = false;
      [...owned.values()].forEach(operation => operation.cancel());
    };
  }, [session]);
  useLayoutEffect(() => {
    [...operations.current.values()].forEach(operation => {
      if (!operation.current()) operation.cancel();
    });
  });

  return useCallback(key => {
    const input = latest.current;
    const source = input.requests.find(request => request.key === key);
    if (!mounted.current || !source?.url || input.session !== getActiveSessionRevision()) return;
    const request = { ...source };
    const capturedIdentity = identity(request);
    const previous = operations.current.get(capturedIdentity);
    if (previous?.current()) {
      try { previous.popup.focus(); } catch { /* Focus failure never opens another tab. */ }
      return;
    }
    previous?.cancel();
    let popup;
    try {
      popup = window.open('', '_blank');
      if (!popup) { toast.error('Pop-up blocked. Allow pop-ups to view the document.'); return; }
      popup.opener = null;
      popup.document.open();
      popup.document.write('<!doctype html><html><head><title>Loading original document</title></head><body>Loading original document…</body></html>');
      popup.document.close();
    } catch {
      if (popup) close(popup);
      toast.error('Unable to prepare document viewer.');
      return;
    }
    const controller = new AbortController();
    let timer;
    const operation = { popup, done: false };
    const current = () => !operation.done && mounted.current && !popup.closed
      && operations.current.get(capturedIdentity) === operation
      && latest.current.session === input.session && getActiveSessionRevision() === input.session
      && latest.current.requests.some(item => identity(item) === capturedIdentity);
    operation.current = current;
    const releasePending = () => {
      operation.done = true;
      clearInterval(timer);
      popup.removeEventListener?.('pagehide', operation.cancel);
      controller.abort();
      if (operations.current.get(capturedIdentity) === operation) operations.current.delete(capturedIdentity);
    };
    operation.cancel = () => {
      if (operation.done) return;
      releasePending();
      close(popup);
    };
    operations.current.set(capturedIdentity, operation);
    popup.addEventListener?.('pagehide', operation.cancel);
    timer = setInterval(() => { if (!current()) operation.cancel(); }, 250);
    loadDocumentResource(request.url, { signal: controller.signal }).then(result => {
      if (!current()) { operation.cancel(); return; }
      if (result.state !== MEDIA_AVAILABLE) {
        operation.cancel();
        toast.error(documentFailureMessage(result));
        return;
      }
      let objectUrl;
      try {
        objectUrl = URL.createObjectURL(result.blob);
        // Retire placeholder listeners before native navigation/document.open.
        popup.removeEventListener?.('pagehide', operation.cancel);
        if (request.offlineCopy) {
          const number = escapeHtml(request.orderNumber);
          popup.document.open();
          popup.document.write(`<!doctype html><html><head><title>Offline source document</title><style>html,body{height:100%;margin:0;font-family:system-ui,sans-serif}header{box-sizing:border-box;height:42px;padding:11px 16px;background:#fffbeb;border-bottom:1px solid #fde68a;color:#78350f;font-size:13px;font-weight:600}iframe{display:block;width:100%;height:calc(100% - 42px);border:0}</style></head><body><header>${number ? `Order ${number} — ` : ''}Offline copy — original document, unstamped</header><iframe title="Offline source document" src="${escapeHtml(objectUrl)}"></iframe></body></html>`);
          popup.document.close();
        } else popup.location.href = objectUrl;
        releasePending();
        retainPublishedResource(popup, objectUrl);
      } catch {
        const report = current();
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        operation.cancel();
        if (report) toast.error('Unable to open document. Please try again.');
      }
    }).catch(error => {
      const report = current() && error?.name !== 'AbortError';
      operation.cancel();
      if (report) toast.error('Unable to open document. Please try again.');
    });
  }, []);
}
